import { Injectable } from '@angular/core';
import { ClientConfiguration, CustomerType, UserDataOptions } from '../models/client-configuration';
import { version } from 'src/version';
import { LoggerService } from './logger.service';
import { LocaleService } from './locale.service';
import { AuthService } from './auth.service';
import { Util } from '../util';
import { InfoDialogData } from '../models/info-dialog';
import { ShareType } from '../models/social';
import { AccessibilityFacade } from '../_core/accessibility/state/accessibility.facade';

@Injectable({
  providedIn: 'root',
})
export class ConfigService {
  private readonly CHINA = 'cn';

  devMode = false;
  initError = false;
  opacVersion = version;
  userItems: UserItems[];
  customerType: CustomerType;
  solutionType: string;
  shareIcons = [ShareType.FACEBOOK, ShareType.X, ShareType.LINKEDIN, ShareType.WHATSAPP];
  smartBannerAndroid: string;
  smartBannerIos: string;

  private configuration: ClientConfiguration;
  private availableExperiencesInfoDialogData: InfoDialogData;

  constructor(
    private logger: LoggerService,
    private authService: AuthService,
    private localeService: LocaleService,
    private readonly accessibilityFacade: AccessibilityFacade
  ) {}

  init(config: ClientConfiguration) {
    if (!this.configuration) {
      this.set(config);

      this.accessibilityFacade.setHighContrastEnabled(config.useHighContrast ?? false);

      // FIXME chapuza porque hay una dependencia circular con el auth service y no se puede usar el config service internamente, así que guardamos el valor desde fuera
      this.authService.ssoType = config.ssoType;
      this.authService.isClientDibam = this.isClientDibam();
      this.logger.info('Client configuration', config);
      if (config.customerType === undefined || config.customerType.menuOrder === undefined) {
        // Retrocompaibilidad
        this.customerType = Util.DEFAULT_CUSTOMER_TYPE;
        this.solutionType = Util.DEFAULT_SOLUTION_TYPE;
      } else {
        this.customerType = config.customerType || Util.DEFAULT_CUSTOMER_TYPE;
        this.solutionType = config.solutionType || Util.DEFAULT_SOLUTION_TYPE;
      }

      this.setGamificationLegacyFields(); // TODO: Delete when all clients reach backend version >= 5.1
      this.smartBannerAndroid = config.smartBannerAndroid;
      this.smartBannerIos = config.smartBannerIos;
    }
    this.populateUserItems();
    this.checkUserItemsVisibility();
    this.performCustomConfigurations();
  }

  private set(config: ClientConfiguration) {
    this.configuration = config;
  }

  public getAvailableExperiencesInfoDialogData() {
    return this.availableExperiencesInfoDialogData;
  }

  public setAvailableExperiencesInfoDialogData(infoDialogData: InfoDialogData) {
    this.availableExperiencesInfoDialogData = infoDialogData;
  }

  private populateUserItems() {
    let currentOrder = 0;
    this.userItems = [];
    this.customerType.menuOrder.forEach((menuItem) => {
      currentOrder = this.userItems.length;
      switch (menuItem) {
        case 'HELP':
          this.userItems.push({
            id: '',
            icon: 'help',
            label: 'dashboard.title',
            hide: true,
          });
          break;
        case 'CHECKOUTS':
          this.userItems.push({
            id: 'checkouts',
            icon: 'loan',
            label: 'checkouts.title',
          });
          break;
        case 'HOLDS':
          this.userItems.push({
            id: 'holds',
            icon: 'holds',
            label: 'holds.title',
            hide: !this.configuration.showHolds,
          });
          break;
        case 'LISTS':
          this.userItems.push({
            id: 'lists',
            icon: 'favorite_list',
            label: 'lists.title',
          });
          break;
        case 'SUGGESTIONS':
          this.userItems.push({
            id: 'suggestions',
            icon: 'suggestion',
            label: 'suggestions.title',
          });
          break;
        case 'DESIDERATA':
          this.userItems.push({
            id: 'desiderata',
            icon: 'ring_bell',
            label: 'desiderata.title',
          });
          break;
        case 'HISTORY':
          this.userItems.push({
            id: 'checkouts/history',
            icon: 'loan_history',
            label: 'checkoutHistory.title',
          });
          break;
        case 'VISUALIZATIONS':
          this.userItems.push({
            id: 'visualizations',
            icon: 'view_history',
            label: 'visualizations.title',
          });
          break;
        case 'USER_HISTORY':
          this.userItems.push({
            id: 'history',
            icon: 'history',
            label: 'history.title',
          });
          break;
        case 'SEARCH':
          this.userItems.push({
            id: 'search',
            icon: 'searches',
            label: 'searches.title',
          });
          break;
        case 'STATS':
          this.userItems.push({
            id: 'stats',
            icon: 'statistics',
            label: 'stats.title',
          });
          break;
        case 'EVENTS':
          this.userItems.push({
            id: 'events',
            icon: 'calendar',
            label: 'events.title',
            showNewTag: this.get().showInviteGuestAccount,
          });
          break;
        case 'CAREER_PLANS':
          this.userItems.push({
            id: 'career-plans',
            icon: 'career_plan',
            label: 'careerPlans.title',
            showNewTag: true,
          });
          break;
        case 'CHALLENGES':
          this.userItems.push({
            id: 'challenges',
            icon: 'challenge',
            label: 'user.challenges.title',
          });
          break;
        case 'DEVICES':
          this.userItems.push({
            id: 'devices',
            icon: 'mobile_devices',
            label: 'devices.title',
          });
          break;
        case 'NOTIFICATIONS':
          this.userItems.push({
            id: 'notifications',
            icon: 'notifications',
            label: 'notifications.title',
          });
          break;
        case 'BOOKCLUBS':
          this.userItems.push({
            id: 'experiences',
            icon: 'bookclub',
            label: this.configuration.bookClubName || 'bookclub.title',
          });
          break;
        case 'RANKING_AND_BADGES':
          this.userItems.push({
            id: 'gamification',
            icon: 'medal',
            label: this.get().customLabels.ranking.menuTitle || 'gamification.ranking.rankingAndMedals',
          });
          break;
        case 'DETAILS':
          this.userItems.push({
            id: 'account',
            icon: 'user',
            label: 'account.details.title',
            showNewTag: this.get().showInviteGuestAccount,
          });
          break;
        case 'INTERESTS':
          this.userItems.push({
            id: 'interests',
            icon: 'tastes',
            label: 'interests.title',
          });
          break;
        default:
          this.userItems.push({
            id: '',
            icon: 'help',
            label: 'dashboard.title',
            hide: true,
          });
          break;
      }
    });
  }

  private checkUserItemsVisibility() {
    if (!this.configuration.showStatistics) {
      this.hideItem('stats');
    }

    if (!this.configuration.showChallengeFeatures) {
      this.hideItem('challenges');
    }

    if (this.shouldHideGamificationSection()) {
      this.hideItem('gamification');
    }

    if (!this.configuration.showPurchaseSuggestions) {
      this.hideItem('suggestions');
    }

    if (!this.configuration.showCareerPlanFeatures) {
      this.hideItem('career-plans');
    }

    if (!this.configuration.showDesiderata) {
      this.hideItem('desiderata');
    }

    if (!this.configuration.showDeviceManagement) {
      this.hideItem('devices');
    }

    if (!this.configuration.showCheckoutHistory) {
      this.hideItem('checkouts/history');
    }

    if (!this.configuration.showVisualizationHistory) {
      this.hideItem('visualizations');
    }

    if (!this.configuration.showUserHistory) {
      this.hideItem('history');
    }

    if (!this.configuration.showBookClubSection) {
      this.hideItem('experiences');
    }

    if (this.configuration.showUserData === UserDataOptions.HIDE) {
      this.hideItem('details');
    }

    const hasInterests = this.configuration.userInterests && this.configuration.userInterests.active;
    if (!this.configuration.showInterestsFormMenu || !hasInterests) {
      this.hideItem('interests');
    }

    if (this.isEndeavor()) {
      this.hideItem('holds');
    }
  }

  private performCustomConfigurations() {
    this.ensureTrailingSlashes();
    this.addRemainingSocialIcons();
    this.setConsortiaTopBarConfiguration();
  }

  private ensureTrailingSlashes() {
    this.configuration.bookClubUrl = Util.ensureTrailingSlash(this.configuration.bookClubUrl);
  }

  private addRemainingSocialIcons() {
    const country = (this.configuration.country || '').toLowerCase();

    // TODO: move to a provider in bootstrap
    if (country === this.CHINA) {
      this.shareIcons = [ShareType.QQ, ShareType.WEIBO, ShareType.WEBCHAT];
    }

    this.shareIcons.push(...[ShareType.EMAIL]);
  }

  private setConsortiaTopBarConfiguration() {
    if (this.configuration.consortia) {
      const message = this.configuration.consortiaMessage;
      this.configuration.topBar.show = true;

      if (!message) {
        this.localeService
          .getString('header.consortia')
          .subscribe((value) => (this.configuration.topBar.centerText = value));
      } else {
        this.configuration.topBar.centerText = message;
      }

      if (this.authService.isAuthenticated()) {
        this.configuration.topBar.show = false;
      }
    }
  }

  get(): ClientConfiguration {
    return this.configuration;
  }

  private hideItem(id: string) {
    const item = this.userItems.find((u) => u.id === id);
    if (item) {
      item.hide = true;
    }
  }

  isHidden(id: string): boolean {
    return this.userItems.find((item) => item.id === id).hide;
  }

  isClientMinisterioColombia(): boolean {
    return this.configuration.codCliente === 'E0624';
  }

  isClientDibam(): boolean {
    return this.configuration.codCliente === 'E0314';
  }

  isSaoPaulo(): boolean {
    return this.configuration.codCliente === 'E0764';
  }

  isEndeavor(): boolean {
    return this.configuration.codCliente === 'E0620';
  }

  isCeibal(): boolean {
    return this.configuration.codCliente === 'E0435';
  }

  isLees(): boolean {
    return this.configuration.codCliente === 'E1085';
  }

  isParana(): boolean {
    return this.configuration.codCliente === 'E1079';
  }

  isLearningRoomBecasSantander(): boolean {
    return this.configuration.codCliente === 'E0988';
  }

  isGarriguesCenter(): boolean {
    return this.configuration.codCliente === 'E1023';
  }

  isEleo(): boolean {
    return this.configuration.codCliente === 'E0391';
  }

  isIberoamericana(): boolean {
    return this.configuration.codCliente === 'E1131';
  }

  isGacetaJuridica(): boolean {
    return this.configuration.codCliente === 'E0747';
  }

  isLeiasp(): boolean {
    return this.configuration.codCliente === 'E1141';
  }

  isZinkers(): boolean {
    return this.configuration.codCliente === 'E1162';
  }

  isIteso(): boolean {
    return this.configuration.codCliente === 'E0794';
  }

  isInaem(): boolean {
    return this.configuration.codCliente === 'E0374';
  }

  isCaf(): boolean {
    return this.configuration.codCliente === 'E1169';
  }

  isAulaMadrid(): boolean {
    return this.configuration.codCliente === 'E1155';
  }

  isAulaDigital(): boolean {
    return this.configuration.codCliente === 'E1210';
  }

  isSenac(): boolean {
    return this.configuration.codCliente === 'E1174';
  }

  shouldHideBookClub() {
    return !this.configuration.bookClubUrl || !this.configuration.showExperiencesFeatures;
  }

  shouldHideGamificationSection() {
    return (
      !this.configuration.gmfConfig.showGamificationSection ||
      (!this.configuration.gmfConfig.showRanking &&
        !this.configuration.gmfConfig.showPoints &&
        !this.configuration.gmfConfig.showBadges)
    );
  }

  private setGamificationLegacyFields() {
    // TODO: Delete when all clients reach backend version >= 5.1
    if (this.configuration.gmfConfig == null) {
      this.configuration.gmfConfig = {
        showGamificationSection: this.configuration.showGamificationFeatures,
        rankingScope: this.configuration.rankingScope,
        showRanking: true,
        showPoints: true,
        showBadges: false,
      };
    }
  }

  isAndroidDevice() {
    return navigator.userAgent.match(/Android/i);
  }

  isIOSDevice() {
    return (
      navigator.userAgent.match(/iPhone/i) ||
      navigator.userAgent.match(/iPad/i) ||
      navigator.userAgent.match(/iPod/i) ||
      (/Macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints && navigator.maxTouchPoints > 1)
    );
  }

  notDesktopDevice() {
    return this.isAndroidDevice() || this.isIOSDevice();
  }

  getCertificationEntityId() {
    return this.configuration.certificationEntityId;
  }
}

export interface UserItems {
  id: string;
  icon: string;
  label: string;
  hide?: boolean;
  showNewTag?: boolean;
}
